import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';

import { useAuth } from '@/hooks/useAuth';
import type { GravelMode, RouteMode } from '@/lib/routing';
import { fetchServerSettings, saveServerSettings, type ServerSettings } from '@/lib/settings';

const STORAGE_KEY = 'motogo.settings';
// The zoom slider fires a state update per tick while dragging; collapse a drag into one PUT.
const SAVE_DEBOUNCE_MS = 800;

export const ZOOM_OFFSET_MIN = -10;
export const ZOOM_OFFSET_MAX = 10;
export const ZOOM_OFFSET_DEFAULT = 0;

const ZOOM_BASE_AT_ZERO = 16.5;
const ZOOM_TICK = 0.1;

export type SpeedUnit = 'kph' | 'mph';

// Round-trip distance bounds — mirror the backend's RoutingController limits.
const ROUTE_DISTANCE_MIN = 10;
const ROUTE_DISTANCE_MAX = 500;

// Ride-split gap bounds (minutes) — mirror UserSettings server-side clamps.
const SPLIT_GAP_MIN = 5;
const SPLIT_GAP_MAX = 180;

export interface Settings {
  zoomOffset: number;
  /** Adapt map zoom to speed (zoom out as you go faster). Off = hold the base zoom. */
  speedZoom: boolean;
  /** Draw the other riders in the channel on the map. */
  showPeers: boolean;
  /** Keep the map pointing north instead of rotating to heading while tracking. */
  lockNorth: boolean;
  /** Unit for the speed readout and limit sign. Internals stay in km/h. */
  speedUnit: SpeedUnit;
  /** Default route style the planner and editor open with (changed in Settings, not by using the planner). */
  routeMode: RouteMode;
  /** Default gravel preference for the planner and editor. */
  routeGravel: GravelMode;
  /** Route planner: round-trip toggle state. */
  routeRoundTrip: boolean;
  /** Route planner: round-trip distance slider (km). */
  routeDistanceKm: number;
  /** Start ride recording automatically on sustained movement. */
  autoRecord: boolean;
  /** Minutes of stationary/offline gap after which a ride is split in two. */
  rideSplitGapMin: number;
}

const DEFAULT_SETTINGS: Settings = {
  zoomOffset: ZOOM_OFFSET_DEFAULT,
  speedZoom: true,
  showPeers: true,
  lockNorth: false,
  speedUnit: 'kph',
  routeMode: 'fast',
  routeGravel: 'neutral',
  routeRoundTrip: false,
  routeDistanceKm: 100,
  autoRecord: true,
  rideSplitGapMin: 30,
};

interface SettingsContextValue {
  settings: Settings;
  setZoomOffset: (offset: number) => void;
  /** Patch one or more settings; values are normalised before they're applied. */
  updateSettings: (patch: Partial<Settings>) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

function clampOffset(n: number): number {
  if (!Number.isFinite(n)) return ZOOM_OFFSET_DEFAULT;
  const rounded = Math.round(n);
  return Math.min(ZOOM_OFFSET_MAX, Math.max(ZOOM_OFFSET_MIN, rounded));
}

/** Coerce every key into a valid value, so a corrupt cache or stale server row can't
 *  poison the running state. Callers merge with {@link DEFAULT_SETTINGS} first so no
 *  field is undefined here. */
function normalizeSettings(s: Settings): Settings {
  return {
    zoomOffset: clampOffset(s.zoomOffset),
    speedZoom: Boolean(s.speedZoom),
    showPeers: Boolean(s.showPeers),
    lockNorth: Boolean(s.lockNorth),
    speedUnit: s.speedUnit === 'mph' ? 'mph' : 'kph',
    routeMode: s.routeMode === 'scenic' || s.routeMode === 'curvy' ? s.routeMode : 'fast',
    routeGravel:
      s.routeGravel === 'exclude' || s.routeGravel === 'include' ? s.routeGravel : 'neutral',
    routeRoundTrip: Boolean(s.routeRoundTrip),
    routeDistanceKm: Number.isFinite(s.routeDistanceKm)
      ? Math.min(ROUTE_DISTANCE_MAX, Math.max(ROUTE_DISTANCE_MIN, Math.round(s.routeDistanceKm)))
      : DEFAULT_SETTINGS.routeDistanceKm,
    autoRecord: Boolean(s.autoRecord),
    rideSplitGapMin: Number.isFinite(s.rideSplitGapMin)
      ? Math.min(SPLIT_GAP_MAX, Math.max(SPLIT_GAP_MIN, Math.round(s.rideSplitGapMin)))
      : DEFAULT_SETTINGS.rideSplitGapMin,
  };
}

/** Known keys only, for the server round-trip (unknown keys ride along via extraRef). */
function toServerSettings(s: Settings): ServerSettings {
  return {
    zoomOffset: s.zoomOffset,
    speedZoom: s.speedZoom,
    showPeers: s.showPeers,
    lockNorth: s.lockNorth,
    speedUnit: s.speedUnit,
    routeMode: s.routeMode,
    routeGravel: s.routeGravel,
    routeRoundTrip: s.routeRoundTrip,
    routeDistanceKm: s.routeDistanceKm,
    autoRecord: s.autoRecord,
    rideSplitGapMin: s.rideSplitGapMin,
  };
}

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return normalizeSettings({ ...DEFAULT_SETTINGS, ...parsed });
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function storeSettings(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage may be unavailable (private mode, quota) — non-fatal
  }
}

export function zoomBaseFromOffset(offset: number): number {
  return ZOOM_BASE_AT_ZERO + clampOffset(offset) * ZOOM_TICK;
}

/**
 * Settings with cross-device sync. localStorage is always written (instant render on
 * next load, and the only store for guests); signed-in sessions additionally sync with
 * `GET/PUT /me/settings`: one pull on mount (server wins — it holds the cross-device
 * truth), then debounced pushes after local changes. Unknown keys the server returns
 * (settings a newer client wrote) are held aside and sent back on every push.
 */
export const SettingsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { state } = useAuth();
  const isAuthenticated = state.status === 'authenticated';

  const [settings, setSettings] = useState<Settings>(loadSettings);

  // Unknown server keys, round-tripped on save so this client doesn't strip them.
  const extraRef = useRef<Record<string, unknown>>({});
  // Set only by a local change, so the push effect never fires for state updates that
  // came from the server pull (which would just echo the value straight back).
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    fetchServerSettings()
      .then((server) => {
        if (cancelled || !server) return;
        const {
          zoomOffset,
          speedZoom,
          showPeers,
          lockNorth,
          speedUnit,
          routeMode,
          routeGravel,
          routeRoundTrip,
          routeDistanceKm,
          autoRecord,
          rideSplitGapMin,
          ...extra
        } = server;
        extraRef.current = extra;

        const hasServerSettings =
          zoomOffset != null ||
          speedZoom != null ||
          showPeers != null ||
          lockNorth != null ||
          speedUnit != null ||
          routeMode != null ||
          routeGravel != null ||
          routeRoundTrip != null ||
          routeDistanceKm != null ||
          autoRecord != null ||
          rideSplitGapMin != null;

        if (hasServerSettings) {
          // Server wins over the localStorage cache — unless the user already changed
          // something this session, which would otherwise be clobbered mid-interaction.
          // Missing keys (e.g. an account synced before this client added them) fall back
          // to defaults rather than null.
          if (!dirtyRef.current) {
            const next = normalizeSettings({
              zoomOffset: zoomOffset ?? DEFAULT_SETTINGS.zoomOffset,
              speedZoom: speedZoom ?? DEFAULT_SETTINGS.speedZoom,
              showPeers: showPeers ?? DEFAULT_SETTINGS.showPeers,
              lockNorth: lockNorth ?? DEFAULT_SETTINGS.lockNorth,
              speedUnit: (speedUnit as SpeedUnit) ?? DEFAULT_SETTINGS.speedUnit,
              routeMode: (routeMode as RouteMode) ?? DEFAULT_SETTINGS.routeMode,
              routeGravel: (routeGravel as GravelMode) ?? DEFAULT_SETTINGS.routeGravel,
              routeRoundTrip: routeRoundTrip ?? DEFAULT_SETTINGS.routeRoundTrip,
              routeDistanceKm: routeDistanceKm ?? DEFAULT_SETTINGS.routeDistanceKm,
              autoRecord: autoRecord ?? DEFAULT_SETTINGS.autoRecord,
              rideSplitGapMin: rideSplitGapMin ?? DEFAULT_SETTINGS.rideSplitGapMin,
            });
            setSettings(next);
            storeSettings(next);
          }
        } else {
          // Nothing synced yet (fresh account): seed the server from this device so prefs
          // tuned before signing in reach the user's other devices.
          const local = loadSettings();
          const differsFromDefault = (Object.keys(DEFAULT_SETTINGS) as (keyof Settings)[]).some(
            (k) => local[k] !== DEFAULT_SETTINGS[k],
          );
          if (differsFromDefault) {
            saveServerSettings({ ...extra, ...toServerSettings(local) }).catch((err) =>
              console.warn('[settings] initial sync failed', err),
            );
          }
        }
      })
      .catch((err) => {
        // Offline or transient API failure — the localStorage value stays in effect,
        // and the next session retries. Sync is best-effort by design.
        console.warn('[settings] fetch failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Persist locally on every change; push to the server only for user-initiated ones.
  useEffect(() => {
    storeSettings(settings);
    if (!isAuthenticated || !dirtyRef.current) return;
    const id = window.setTimeout(() => {
      dirtyRef.current = false;
      saveServerSettings({ ...extraRef.current, ...toServerSettings(settings) }).catch((err) => {
        // Best-effort: the value stays cached locally for this device, but if no later
        // change pushes successfully, next session's server-wins pull reverts it here too.
        console.warn('[settings] sync failed', err);
      });
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [settings, isAuthenticated]);

  const updateSettings = useCallback((patch: Partial<Settings>) => {
    dirtyRef.current = true;
    setSettings((prev) => normalizeSettings({ ...prev, ...patch }));
  }, []);

  const setZoomOffset = useCallback(
    (offset: number) => updateSettings({ zoomOffset: offset }),
    [updateSettings],
  );

  const value = useMemo(
    () => ({ settings, setZoomOffset, updateSettings }),
    [settings, setZoomOffset, updateSettings],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within a SettingsProvider');
  return ctx;
}
